/*
 *
 * PblManagement Sagas
 *
 * @version 1.0.0
 * @author Davide Polci
 * @since 30-Jun-2025
 */

import { all, call, put, select, take } from 'redux-saga/effects'
import * as api from 'Api/pblManagement/pblManagement'
import * as actions from '../actions/pblManagement'
import moment from 'moment'
import history from 'Utility/history'
import { routesList } from 'Schemas/routesList'
import { doPopup } from 'Store/actions/popup'
import { getFormikForm, getUserMerchantId, getUserRoles, getUserTenantId } from 'Store/selectors'
import { deleteEmptyValues, userRolesString, exportDownload, buildRSQLQuery } from 'Utility/globalMethods'
import { dateFromUTCtoTerminalTimeZone, italianDateConverter, momentLongDateFormat } from 'Utility/dateMethods'
import { doLocalLoading } from 'Store/actions/loading'
import formattedMessage from 'Route/PblManagement/modules/languageModule'
import { parseStringNumber } from 'Utility/numberMethods'

const popupSuccessResult = {
  title: formattedMessage.done,
  description: formattedMessage.successMessage,
  isSuccess: true
}

const popupErrorResult = {
  title: formattedMessage.exportResultFailedTitle,
  isError: true
}

const popupFailedSettingsCalled = {
  title: formattedMessage.exportResultFailedTitle,
  isError: true,
  primaryButtonLabel: formattedMessage.close,
  onClose: {
    type: 'ACTION',
    payload: () => history.push(routesList.goToDashboard)
  },
  primaryAction: {
    type: 'ACTION',
    payload: () => history.push(routesList.goToDashboard)
  }
}

export function* getPblsListSaga(payload) {
  const { page = 1, sorting } = payload || {}
  const form = yield select((state) => getFormikForm(state, 'pblManagementForm'))
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roles = yield select(getUserRoles)
  const formatDate = momentLongDateFormat()

  const excludedKeys = [
    'minAmount',
    'maxAmount',
    'searchByDate',
    'fromTime',
    'toTime',
    'hourMinutesFromTime',
    'hourMinutesToTime'
  ]

  const rsqlArray = Object.entries(form || {}) // TODO: The ottimization of this part is mandatory!!!!!!!!!!!!!!
    .filter(([key, value]) => value !== null && value !== undefined && value !== '' && !excludedKeys.includes(key))
    .map(([key, value]) => {
      if (value === null || value === undefined || value === '') return null
      return {
        field: key,
        operator: '==',
        value: value
      }
    })
    .filter(Boolean)

  if (!rsqlArray.some((item) => item.field === 'status')) {
    rsqlArray.push({
      field: 'status',
      operator: '!=',
      value: 'DELETED'
    })
  }

  const pushBaseRange = (min, max, field, array) => {
    if (!field || !array) return
    if (min && max) {
      array.push({
        field,
        operator: 'bt',
        value: [min, max]
      })
    } else {
      if (min) {
        array.push({
          field,
          operator: 'ge',
          value: min
        })
      }
      if (max) {
        array.push({
          field,
          operator: 'le',
          value: max
        })
      }
    }
  }

  // AMOUNT - predefinedAmounts
  pushBaseRange(parseStringNumber(form?.minAmount), parseStringNumber(form?.maxAmount), 'amount', rsqlArray)
  const dateType = {
    CREATION: 'createdAt',
    UPDATED: 'updatedAt',
    EXPIRATION: 'expirationDateTime'
  }
  const formattedStartDate = form?.fromTime ? moment(form?.fromTime, formatDate).set({
    hour: moment(form?.hourMinutesFromTime)?.hour(),
    minute: moment(form?.hourMinutesFromTime)?.minute(),
    second: 0,
    millisecond: 0
  }) : null

  const formattedEndDate = form?.toTime ? moment(form?.toTime, formatDate).set({
    hour: moment(form?.hourMinutesToTime)?.hour(),
    minute: moment(form?.hourMinutesToTime)?.minute(),
    second: 0,
    millisecond: 0
  }) : null

  // DATE & TIME
  if (form?.searchByDate && (form?.fromTime || form?.toTime)) {
    let start = italianDateConverter(formattedStartDate, 'time')
    let end = italianDateConverter(moment(formattedEndDate).set({ second: 59 }), 'time')
    pushBaseRange(start, end, dateType[form?.searchByDate] || undefined, rsqlArray)
  }

  const options = deleteEmptyValues({
    sort: sorting?.orderBy && sorting?.sortIn ? `${sorting?.orderBy},${sorting?.sortIn.toLowerCase()}` : '',
    pageSize: 10,
    page: page > 0 ? page : null
  })

  const params = deleteEmptyValues({
    rsqlString: buildRSQLQuery(rsqlArray, options),
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(roles)
    }
  })

  yield put(doLocalLoading({ id: 'pblManagement', value: true }))
  yield put(actions.getPblsList.request({ ...params }))
  const { error, response } = yield call(api.callGetPbls, params)
  if (!error) {
    yield put(actions.getPblsList.success(response, response.data))
  } else {
    yield put(actions.getPblsList.failure(error, response))
  }
  yield put(doLocalLoading({ id: 'pblManagement', value: false }))
}

export function* deleteOrUpdateLinkSaga() {
  yield put(doLocalLoading({ id: 'deleteOrUpdatePbl', value: true }))
  const formatDate = momentLongDateFormat()
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roles = yield select(getUserRoles)
  const form = yield select((state) => getFormikForm(state, 'PblManagementActionForm'))

  const formattedExpDate = form?.expirationDateTime ? moment(form?.expirationDateTime, formatDate).set({
    hour: moment(form?.expTime)?.hour(),
    minute: moment(form?.expTime)?.minute(),
    second: 59,
    // millisecond: 999
  }) : null
  const expDate = italianDateConverter(formattedExpDate, 'time')
  const params = deleteEmptyValues({
    data: {
      maxNumberOfPayments: form?.maxPayments || 0,
      expirationDateTime: expDate,
      status: form?.status || ''
    },
    urlParams: {
      tenantId,
      merchantId,
      linkId: form?.longTermSessionId || ''
    },
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(roles)
    }
  })

  yield put(actions.deleteOrUpdateLink.request(params))
  const { error, response } = yield call(api.deleteOrUpdateLink, params)

  if (!error) {
    yield put(actions.deleteOrUpdateLink.success(response, response.data))
    yield put(doPopup('RESULT_POPUP', popupSuccessResult))
    yield put(actions.doFetchPblsList())
  } else {
    yield put(actions.deleteOrUpdateLink.failure(error, response))
    yield put(doPopup(''))
    yield put(doPopup('RESULT_POPUP', popupErrorResult))
  }
  yield put(doLocalLoading({ id: 'deleteOrUpdatePbl', value: false }))
}

export function* pblExportSaga(payload) {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const rolesList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    queryParams: {
      fromDate: payload?.date?.from, //TODO: FIX THIS PART
      toDate: moment(payload?.date.to).set('second', 59).toISOString()
    },
    headers: {
      tenantId,
      merchantId,
      roles: userRolesString(rolesList)
    },
    format: payload.format.toUpperCase()
  })
  yield put(doLocalLoading({ id: 'exportPbls', value: true }))
  yield put(actions.retrievePblsExportFile.request(params))
  const { error, response } = yield call(api.callRetrievePblsExportFile, params)
  if (!error) {
    yield put(actions.retrievePblsExportFile.success(response.data))
    if (response?.data?.size === 0) {
      yield put(
        doPopup('RESULT_POPUP', {
          ...popupErrorResult,
          description: formattedMessage.exportResultDescriptionEmpty
        })
      )
    } else {
      const fileName = response?.headers?.['content-disposition']
        ? response.headers['content-disposition'].match(/filename="(.+)"/)[1]
        : `XPay_Pbls_MerchantID_From_${dateFromUTCtoTerminalTimeZone(
          payload?.date.from,
          'DD_MM_YYYY-HH:mm'
        )}_To_${dateFromUTCtoTerminalTimeZone(payload?.date.to, 'DD_MM_YYYY-HH:mm')}.${
          payload?.format === 'csv' ? 'zip' : 'xlsx'
        }`
      const fileType = response?.headers?.['content-type']
        ? response.headers['content-type']
        : payload?.format === 'csv'
          ? 'application/zip'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

      exportDownload(response.data, fileName, fileType)
      yield put(doPopup(''))
    }
  } else {
    if (error.status === 400 && error?.data['error-code'] === 'PS0064') {
      yield put(
        doPopup('RESULT_POPUP', {
          ...popupErrorResult,
          description: formattedMessage.exportResultDescriptionExcede
        })
      )
      yield put(actions.retrievePblsExportFile.failure(response, { ...error, ignoreSystemError: true }))
    } else if (error.status === 400 && error?.data['error-code'] === 'PS0065') {
      yield put(
        doPopup('RESULT_POPUP', {
          ...popupErrorResult,
          description: formattedMessage.exportResultDescriptionEmpty
        })
      )
      yield put(actions.retrievePblsExportFile.failure(response, { ...error, ignoreSystemError: true }))
    } else {
      yield put(doPopup(''))
      yield put(actions.retrievePblsExportFile.failure(response, error))
    }
  }
  yield put(doLocalLoading({ id: 'exportPbls', value: false }))
}

export function* getPblDetailsSaga(payload) {
  const { linkId, page = 1, isUsesTable } = payload
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const roleList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    headers: {
      merchantId,
      tenantId,
      roles: userRolesString(roleList)
    },
    urlParams: {
      linkId
    },
    queryParams: {
      pageSize: 10,
      page
    }
  })

  yield put(doLocalLoading({ id: isUsesTable ? 'PblManagementGetPblDetailsUsesInfo' : 'PblManagementGetPblDetails', value: true }))
  yield put(actions.getPblDetails.request({ ...params }))
  const { error, response } = yield call(api.callGetPblDetail, params)
  if (!error) {
    yield put(actions.getPblDetails.success(response, response.data))
  } else {
    yield put(actions.getPblDetails.failure(error, response))
  }
  yield put(doLocalLoading({ id: isUsesTable ? 'PblManagementGetPblDetailsUsesInfo' : 'PblManagementGetPblDetails', value: false }))
}

export function* getPblVersionSaga({ callback } = {}) {
  const tenantId = yield select(getUserTenantId)
  const merchantId = yield select(getUserMerchantId)
  const rolesList = yield select(getUserRoles)

  const params = deleteEmptyValues({
    headers: {
      tenantId,
      merchantId,
      // terminalId: '95001868',
      roles: userRolesString(rolesList)
    }
  })
  yield put(actions.getPblVersionSettings.request(params))
  const { error, response } = yield call(api.callPblVersionSettings, params)
  if (!error) {
    yield put(actions.getPblVersionSettings.success(response.data))
    callback?.()
  } else {
    yield put(actions.getPblVersionSettings.failure(response, error))
    yield put(doPopup(''))
    yield put(doPopup('GENERIC_POPUP', { ...popupFailedSettingsCalled }))
  }
}

// @generator saga

// @generator saga:watch

export function* watchGetPblsList() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_PBLS)
    yield call(getPblsListSaga, payload)
  }
}
export function* watchDeleteOrUpdateLink() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_DELETE_OR_UPDATE_LINK)
    yield call(deleteOrUpdateLinkSaga, payload)
  }
}
export function* watchPblExport() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_EXPORT_PBL)
    yield call(pblExportSaga, { ...payload })
  }
}

export function* watchGetPblDetails() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_PBL_DETAILS)
    yield call(getPblDetailsSaga, { ...payload })
  }
}


export function* watchGetPblVSettings() {
  while (true) {
    const { payload } = yield take(actions.actionTypes.DO_FETCH_PBL_VERSION_SETTINGS)
    yield call(getPblVersionSaga, { ...payload })
  }
}
/**
 * Root saga manages watcher lifecycle
 */
export default function* rootSaga() {
  yield all([
    watchGetPblsList(),
    watchDeleteOrUpdateLink(),
    watchPblExport(),
    watchGetPblDetails(),
    watchGetPblVSettings(),
    // @generator saga:fork
  ])
}
